import { c as _c } from "react/compiler-runtime";
import React, { FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonStandard, ButtonStandardType } from 'record-it-components/button/ButtonStandard/ButtonStandard';
import { FormGroup } from 'record-it-components/form/FormGroup/FormGroup';
import { useEventCallback } from 'record-it-components/hooks/useEventCallback';
import { Accordion } from 'record-it-components/layout/Accordion/Accordion';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { EntityName } from 'record-it-time-common/EntityName';
import { assertNotNullOrUndefined } from 'record-it-utils/utils/assert/assertNotNullOrUndefined';
import { DownloadUtils } from 'record-it-utils/utils/DownloadUtils/DownloadUtils';
import { StringUtils } from 'record-it-utils/utils/StringUtils/StringUtils';
import { ApiAdapter } from '../../../classes/ApiAdapter/ApiAdapter';
import { useEntities } from '../../../hooks/entity/useEntities/useEntities';
import { useEntityRepository } from '../../../hooks/entity/useEntityRepository';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import { ConstructionSiteSelect } from '../../input/ConstructionSiteSelect/ConstructionSiteSelect';
import { Month, allMonths } from '../../input/MonthSelect/Month';
import { MonthSelect } from '../../input/MonthSelect/MonthSelect';
import { UserGroupSelect } from '../../input/UserGroupSelect/UserGroupSelect';
import { ExportWorkTimeBaseDialog, ExportWorkTimeBaseDialogProps } from '../ExportWorkTimeBaseDialog/ExportWorkTimeBaseDialog';
import styles from '../ExportWorkTimeBaseDialog/ExportWorkTimeBaseDialog.module.css';
import { ExportYearSelect } from '../ExportYearSelect/ExportYearSelect';
export type ExportAdminWorkTimeDialogProps = PropsWithoutChildren<Omit<ExportWorkTimeBaseDialogProps, 'showLoading'>>;
export const ExportAdminWorkTimeDialog: FunctionComponent<ExportAdminWorkTimeDialogProps> = React.memo(props => {
  const $ = _c(144);
  const {
    t
  } = useTranslation();
  const {
    getRequiredCurrentUserPermissions
  } = useCurrentUser();
  let t0;
  if ($[0] !== getRequiredCurrentUserPermissions) {
    t0 = getRequiredCurrentUserPermissions().getUserGroupsForExport();
    $[0] = getRequiredCurrentUserPermissions;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const userGroups = t0;
  const constructionSites = useEntities(EntityName.ConstructionSite, "getAll");
  const [constructionSiteExportOpen, setConstructionSiteExportOpen] = useState(true);
  const [constructionSiteSummaryExportOpen, setConstructionSiteSummaryExportOpen] = useState(true);
  const [activitiesExportOpen, setActivitiesExportOpen] = useState(true);
  const [allTimesheetsExportOpen, setAllTimesheetsExportOpen] = useState(true);
  const [fullExportOpen, setFullExportOpen] = useState(true);
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Date().getFullYear();
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const [allTimesheetsYear, setAllTimesheetsYear] = useState(t1);
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = new Date().getMonth();
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [allTimesheetsMonth, setAllTimesheetsMonth] = useState(t2 + 1);
  const [allTimesheetsUserGroupId, setAllTimesheetsUserGroupId] = useState(null);
  const [loading, setLoading] = useState(false);
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = new Date().getFullYear();
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const [constructionSiteYear, setConstructionSiteYear] = useState(t3);
  const [constructionSiteUserGroupId, setConstructionSiteUserGroupId] = useState(userGroups[0]?.id ?? null);
  const [constructionSiteId, setConstructionSiteId] = useState(null);
  const constructionSiteRepository = useEntityRepository(EntityName.ConstructionSite);
  const constructionSiteExportEnabled = !!constructionSiteUserGroupId;
  const constructionSiteSummaryExportEnabled = !!constructionSiteId;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = new Date().getFullYear();
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const [activitiesYear, setActivitiesYear] = useState(t4);
  const [activitiesUserGroupId, setActivitiesUserGroupId] = useState(userGroups[0]?.id ?? null);
  const activitiesExportEnabled = !!activitiesUserGroupId;
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = new Date().getFullYear();
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const [fullExportYear, setFullExportYear] = useState(t5);
  const [fullExportUserGroupId, setFullExportUserGroupId] = useState(userGroups[0]?.id ?? null);
  const fullExportEnabled = !!fullExportUserGroupId;
  let t6;
  if ($[7] !== t) {
    t6 = year => t("components.workTime.ExportAdminWorkTimeDialog.exports.constructionSite.fileName", {
      year
    });
    $[7] = t;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const getConstructionSiteExportFileName = t6;
  let t7;
  if ($[9] !== t) {
    t7 = name => t("components.workTime.ExportAdminWorkTimeDialog.exports.constructionSiteSummary.fileName", {
      name
    });
    $[9] = t;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const getConstructionSiteSummaryExportFileName = t7;
  let t8;
  if ($[11] !== t) {
    t8 = year_0 => t("components.workTime.ExportAdminWorkTimeDialog.exports.activities.fileName", {
      year: year_0
    });
    $[11] = t;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const getActivitiesExportFileName = t8;
  let t9;
  if ($[13] !== constructionSiteUserGroupId || $[14] !== constructionSiteYear || $[15] !== getConstructionSiteExportFileName) {
    t9 = () => {
      assertNotNullOrUndefined(constructionSiteUserGroupId, "cannot handleConstructionSiteExportCLick without constructionSiteUserGroup");
      setLoading(true);
      ApiAdapter.exportWorkTimeConstructionSite({
        year: constructionSiteYear,
        userGroupId: constructionSiteUserGroupId
      }).then(response => {
        const filename = getConstructionSiteExportFileName(constructionSiteYear);
        DownloadUtils.download(filename, response);
      }).finally(() => {
        setLoading(false);
      });
    };
    $[13] = constructionSiteUserGroupId;
    $[14] = constructionSiteYear;
    $[15] = getConstructionSiteExportFileName;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const handleConstructionSiteExportClick = useEventCallback(t9);
  let t10;
  if ($[17] !== constructionSiteId || $[18] !== constructionSiteRepository || $[19] !== getConstructionSiteSummaryExportFileName) {
    t10 = () => {
      assertNotNullOrUndefined(constructionSiteId, "cannot handleConstructionSiteSummaryExportClick without constructionSiteId");
      setLoading(true);
      ApiAdapter.exportWorkTimeConstructionSiteSummary({
        constructionSiteId
      }).then(response_0 => {
        const constructionSiteName = constructionSiteRepository.getRequiredById(constructionSiteId).name;
        const fileName = getConstructionSiteSummaryExportFileName(constructionSiteName);
        DownloadUtils.download(fileName, response_0);
      }).finally(() => {
        setLoading(false);
      });
    };
    $[17] = constructionSiteId;
    $[18] = constructionSiteRepository;
    $[19] = getConstructionSiteSummaryExportFileName;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const handleConstructionSiteSummaryExportClick = useEventCallback(t10);
  let t11;
  if ($[21] !== activitiesUserGroupId || $[22] !== activitiesYear || $[23] !== getActivitiesExportFileName) {
    t11 = () => {
      assertNotNullOrUndefined(activitiesUserGroupId, "cannot handleActivitiesExportClick without activitiesUserGroupId");
      setLoading(true);
      ApiAdapter.exportWorkTimeActivities({
        year: activitiesYear,
        userGroupId: activitiesUserGroupId
      }).then(response_1 => {
        const filename_0 = getActivitiesExportFileName(activitiesYear);
        DownloadUtils.download(filename_0, response_1);
      }).finally(() => {
        setLoading(false);
      });
    };
    $[21] = activitiesUserGroupId;
    $[22] = activitiesYear;
    $[23] = getActivitiesExportFileName;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const handleActivitiesExportClick = useEventCallback(t11);
  let t12;
  if ($[25] !== allTimesheetsMonth || $[26] !== allTimesheetsUserGroupId || $[27] !== allTimesheetsYear || $[28] !== t) {
    t12 = () => {
      assertNotNullOrUndefined(allTimesheetsUserGroupId, "cannot handleAllTimesheetsExportClick without allTimesheetsUserGroupId");
      setLoading(true);
      ApiAdapter.exportWorkTimeAllTimesheets({
        daterange: {
          year: allTimesheetsYear,
          month: allTimesheetsMonth
        },
        forUserGroupId: allTimesheetsUserGroupId
      }).then(response_2 => {
        const filename_1 = t("components.workTime.ExportAdminWorkTimeDialog.exports.allTimesheetsForUserGroup.fileName", {
          year: allTimesheetsYear,
          month: StringUtils.padLeft(allTimesheetsMonth.toString(), 2)
        });
        DownloadUtils.download(filename_1, response_2);
      }).finally(() => {
        setLoading(false);
      });
    };
    $[25] = allTimesheetsMonth;
    $[26] = allTimesheetsUserGroupId;
    $[27] = allTimesheetsYear;
    $[28] = t;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  const handleAllTimesheetsExportClick = useEventCallback(t12);
  let t13;
  if ($[30] !== fullExportUserGroupId || $[31] !== fullExportYear || $[32] !== t) {
    t13 = () => {
      assertNotNullOrUndefined(fullExportUserGroupId, "cannot handleFullExportClick without fullExportUserGroupId");
      setLoading(true);
      ApiAdapter.exportWorkTimeFullExport({
        userGroupId: fullExportUserGroupId,
        year: fullExportYear
      }).then(response_3 => {
        const fileName_0 = t("components.workTime.ExportAdminWorkTimeDialog.exports.fullExport.fileName", {
          year: fullExportYear
        });
        DownloadUtils.download(fileName_0, response_3);
      }).finally(() => {
        setLoading(false);
      });
    };
    $[30] = fullExportUserGroupId;
    $[31] = fullExportYear;
    $[32] = t;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  const handleFullExportClick = useEventCallback(t13);
  let t14;
  if ($[34] !== t) {
    t14 = t("components.workTime.ExportAdminWorkTimeDialog.exports.constructionSite.title");
    $[34] = t;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== constructionSiteYear) {
    t15 = <ExportYearSelect year={constructionSiteYear} onYearSelected={setConstructionSiteYear} />;
    $[36] = constructionSiteYear;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== constructionSiteUserGroupId || $[39] !== userGroups) {
    t16 = <UserGroupSelect userGroupId={constructionSiteUserGroupId} onUserGroupChanged={setConstructionSiteUserGroupId} availableUserGroups={userGroups} />;
    $[38] = constructionSiteUserGroupId;
    $[39] = userGroups;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== t15 || $[42] !== t16) {
    t17 = <FormGroup><div className={styles.exportInputForm} data-testid="ExportAdminWorkTimeDialog.ConstructionSiteExport">{t15}{t16}</div></FormGroup>;
    $[41] = t15;
    $[42] = t16;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  const t18 = !constructionSiteExportEnabled;
  let t19;
  if ($[44] !== t) {
    t19 = t("components.workTime.ExportWorkTimeBaseDialog.export");
    $[44] = t;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  let t20;
  if ($[46] !== handleConstructionSiteExportClick || $[47] !== t18 || $[48] !== t19) {
    t20 = <ButtonStandard type={ButtonStandardType.Primary} disabled={t18} onClick={handleConstructionSiteExportClick} data-testid="ExportAdminWorkTimeDialog.ConstructionSite.ExportButton">{t19}</ButtonStandard>;
    $[46] = handleConstructionSiteExportClick;
    $[47] = t18;
    $[48] = t19;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  let t21;
  if ($[50] !== constructionSiteExportOpen || $[51] !== t14 || $[52] !== t17 || $[53] !== t20) {
    t21 = <Accordion summary={t14} open={constructionSiteExportOpen} onToggle={setConstructionSiteExportOpen}>{t17}{t20}</Accordion>;
    $[50] = constructionSiteExportOpen;
    $[51] = t14;
    $[52] = t17;
    $[53] = t20;
    $[54] = t21;
  } else {
    t21 = $[54];
  }
  let t22;
  if ($[55] !== t) {
    t22 = t("components.workTime.ExportAdminWorkTimeDialog.exports.constructionSiteSummary.title");
    $[55] = t;
    $[56] = t22;
  } else {
    t22 = $[56];
  }
  let t23;
  if ($[57] !== constructionSiteId || $[58] !== constructionSites) {
    t23 = <FormGroup><div className={styles.exportInputForm}><ConstructionSiteSelect availableConstructionSites={constructionSites} constructionSiteId={constructionSiteId} onConstructionSiteChanged={setConstructionSiteId} /></div></FormGroup>;
    $[57] = constructionSiteId;
    $[58] = constructionSites;
    $[59] = t23;
  } else {
    t23 = $[59];
  }
  const t24 = !constructionSiteSummaryExportEnabled;
  let t25;
  if ($[60] !== t) {
    t25 = t("components.workTime.ExportWorkTimeBaseDialog.export");
    $[60] = t;
    $[61] = t25;
  } else {
    t25 = $[61];
  }
  let t26;
  if ($[62] !== handleConstructionSiteSummaryExportClick || $[63] !== t24 || $[64] !== t25) {
    t26 = <ButtonStandard type={ButtonStandardType.Primary} disabled={t24} onClick={handleConstructionSiteSummaryExportClick}>{t25}</ButtonStandard>;
    $[62] = handleConstructionSiteSummaryExportClick;
    $[63] = t24;
    $[64] = t25;
    $[65] = t26;
  } else {
    t26 = $[65];
  }
  let t27;
  if ($[66] !== constructionSiteSummaryExportOpen || $[67] !== t22 || $[68] !== t23 || $[69] !== t26) {
    t27 = <Accordion summary={t22} open={constructionSiteSummaryExportOpen} onToggle={setConstructionSiteSummaryExportOpen}>{t23}{t26}</Accordion>;
    $[66] = constructionSiteSummaryExportOpen;
    $[67] = t22;
    $[68] = t23;
    $[69] = t26;
    $[70] = t27;
  } else {
    t27 = $[70];
  }
  let t28;
  if ($[71] !== t) {
    t28 = t("components.workTime.ExportAdminWorkTimeDialog.exports.activities.title");
    $[71] = t;
    $[72] = t28;
  } else {
    t28 = $[72];
  }
  let t29;
  if ($[73] !== activitiesYear) {
    t29 = <ExportYearSelect year={activitiesYear} onYearSelected={setActivitiesYear} />;
    $[73] = activitiesYear;
    $[74] = t29;
  } else {
    t29 = $[74];
  }
  let t30;
  if ($[75] !== activitiesUserGroupId || $[76] !== userGroups) {
    t30 = <UserGroupSelect userGroupId={activitiesUserGroupId} onUserGroupChanged={setActivitiesUserGroupId} availableUserGroups={userGroups} />;
    $[75] = activitiesUserGroupId;
    $[76] = userGroups;
    $[77] = t30;
  } else {
    t30 = $[77];
  }
  let t31;
  if ($[78] !== t29 || $[79] !== t30) {
    t31 = <FormGroup><div className={styles.exportInputForm}>{t29}{t30}</div></FormGroup>;
    $[78] = t29;
    $[79] = t30;
    $[80] = t31;
  } else {
    t31 = $[80];
  }
  const t32 = !activitiesExportEnabled;
  let t33;
  if ($[81] !== t) {
    t33 = t("components.workTime.ExportWorkTimeBaseDialog.export");
    $[81] = t;
    $[82] = t33;
  } else {
    t33 = $[82];
  }
  let t34;
  if ($[83] !== handleActivitiesExportClick || $[84] !== t32 || $[85] !== t33) {
    t34 = <ButtonStandard type={ButtonStandardType.Primary} disabled={t32} onClick={handleActivitiesExportClick}>{t33}</ButtonStandard>;
    $[83] = handleActivitiesExportClick;
    $[84] = t32;
    $[85] = t33;
    $[86] = t34;
  } else {
    t34 = $[86];
  }
  let t35;
  if ($[87] !== activitiesExportOpen || $[88] !== t28 || $[89] !== t31 || $[90] !== t34) {
    t35 = <Accordion summary={t28} open={activitiesExportOpen} onToggle={setActivitiesExportOpen}>{t31}{t34}</Accordion>;
    $[87] = activitiesExportOpen;
    $[88] = t28;
    $[89] = t31;
    $[90] = t34;
    $[91] = t35;
  } else {
    t35 = $[91];
  }
  let t36;
  if ($[92] !== t) {
    t36 = t("components.workTime.ExportAdminWorkTimeDialog.exports.allTimesheetsForUserGroup.title");
    $[92] = t;
    $[93] = t36;
  } else {
    t36 = $[93];
  }
  let t37;
  if ($[94] !== allTimesheetsYear) {
    t37 = <ExportYearSelect year={allTimesheetsYear} onYearSelected={setAllTimesheetsYear} />;
    $[94] = allTimesheetsYear;
    $[95] = t37;
  } else {
    t37 = $[95];
  }
  let t38;
  if ($[96] !== allTimesheetsMonth) {
    t38 = <MonthSelect availableMonths={allMonths} month={allTimesheetsMonth} onMonthSelected={setAllTimesheetsMonth} />;
    $[96] = allTimesheetsMonth;
    $[97] = t38;
  } else {
    t38 = $[97];
  }
  let t39;
  if ($[98] !== allTimesheetsUserGroupId || $[99] !== userGroups) {
    t39 = <UserGroupSelect userGroupId={allTimesheetsUserGroupId} onUserGroupChanged={setAllTimesheetsUserGroupId} availableUserGroups={userGroups} data-testid="ExportAdminWorkTimeDialog.AllTimesheets.UserGroupSelect" />;
    $[98] = allTimesheetsUserGroupId;
    $[99] = userGroups;
    $[100] = t39;
  } else {
    t39 = $[100];
  }
  let t40;
  if ($[101] !== t37 || $[102] !== t38 || $[103] !== t39) {
    t40 = <FormGroup><div className={styles.exportInputForm} data-testid="ExportAdminWorkTimeDialog.AllTimesheets">{t37}{t38}{t39}</div></FormGroup>;
    $[101] = t37;
    $[102] = t38;
    $[103] = t39;
    $[104] = t40;
  } else {
    t40 = $[104];
  }
  let t41;
  if ($[105] !== t) {
    t41 = t("components.workTime.ExportWorkTimeBaseDialog.export");
    $[105] = t;
    $[106] = t41;
  } else {
    t41 = $[106];
  }
  let t42;
  if ($[107] !== handleAllTimesheetsExportClick || $[108] !== t41) {
    t42 = <ButtonStandard type={ButtonStandardType.Primary} onClick={handleAllTimesheetsExportClick} data-testid="ExportAdminWorkTimeDialog.AllTimesheets.ExportButton">{t41}</ButtonStandard>;
    $[107] = handleAllTimesheetsExportClick;
    $[108] = t41;
    $[109] = t42;
  } else {
    t42 = $[109];
  }
  let t43;
  if ($[110] !== allTimesheetsExportOpen || $[111] !== t36 || $[112] !== t40 || $[113] !== t42) {
    t43 = <Accordion summary={t36} open={allTimesheetsExportOpen} onToggle={setAllTimesheetsExportOpen}>{t40}{t42}</Accordion>;
    $[110] = allTimesheetsExportOpen;
    $[111] = t36;
    $[112] = t40;
    $[113] = t42;
    $[114] = t43;
  } else {
    t43 = $[114];
  }
  let t44;
  if ($[115] !== t) {
    t44 = t("components.workTime.ExportAdminWorkTimeDialog.exports.fullExport.title");
    $[115] = t;
    $[116] = t44;
  } else {
    t44 = $[116];
  }
  let t45;
  if ($[117] !== fullExportYear) {
    t45 = <ExportYearSelect year={fullExportYear} onYearSelected={setFullExportYear} />;
    $[117] = fullExportYear;
    $[118] = t45;
  } else {
    t45 = $[118];
  }
  let t46;
  if ($[119] !== fullExportUserGroupId || $[120] !== userGroups) {
    t46 = <UserGroupSelect userGroupId={fullExportUserGroupId} onUserGroupChanged={setFullExportUserGroupId} availableUserGroups={userGroups} />;
    $[119] = fullExportUserGroupId;
    $[120] = userGroups;
    $[121] = t46;
  } else {
    t46 = $[121];
  }
  let t47;
  if ($[122] !== t45 || $[123] !== t46) {
    t47 = <FormGroup><div className={styles.exportInputForm}>{t45}{t46}</div></FormGroup>;
    $[122] = t45;
    $[123] = t46;
    $[124] = t47;
  } else {
    t47 = $[124];
  }
  const t48 = !fullExportEnabled;
  let t49;
  if ($[125] !== t) {
    t49 = t("components.workTime.ExportWorkTimeBaseDialog.export");
    $[125] = t;
    $[126] = t49;
  } else {
    t49 = $[126];
  }
  let t50;
  if ($[127] !== handleFullExportClick || $[128] !== t48 || $[129] !== t49) {
    t50 = <ButtonStandard type={ButtonStandardType.Primary} onClick={handleFullExportClick} disabled={t48}>{t49}</ButtonStandard>;
    $[127] = handleFullExportClick;
    $[128] = t48;
    $[129] = t49;
    $[130] = t50;
  } else {
    t50 = $[130];
  }
  let t51;
  if ($[131] !== fullExportOpen || $[132] !== t44 || $[133] !== t47 || $[134] !== t50) {
    t51 = <Accordion summary={t44} open={fullExportOpen} onToggle={setFullExportOpen}>{t47}{t50}</Accordion>;
    $[131] = fullExportOpen;
    $[132] = t44;
    $[133] = t47;
    $[134] = t50;
    $[135] = t51;
  } else {
    t51 = $[135];
  }
  let t52;
  if ($[136] !== loading || $[137] !== props || $[138] !== t21 || $[139] !== t27 || $[140] !== t35 || $[141] !== t43 || $[142] !== t51) {
    t52 = <ExportWorkTimeBaseDialog showLoading={loading} {...props}>{t21}{t27}{t35}{t43}{t51}</ExportWorkTimeBaseDialog>;
    $[136] = loading;
    $[137] = props;
    $[138] = t21;
    $[139] = t27;
    $[140] = t35;
    $[141] = t43;
    $[142] = t51;
    $[143] = t52;
  } else {
    t52 = $[143];
  }
  return t52;
});